import type { BaseOptionType } from 'antd/es/select';
import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useState } from 'react';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { filterOption } from '@/std/filter';
import { useDebouncedValue } from '@/std/hooks/useDebouncedValue';
import type { ProdutoCest } from '../types/ProdutoCest';
import { apiProdutoCestGet, type ApiProdutoCestGetParams } from '../api/apiProdutoCestGet';

export function SelectProdutoCest({
    description,
    disabled,
    value,
    onChange,
}: {
    description?: string | null;
    disabled?: boolean;
    value?: string | null;
    onChange?: (cest: ProdutoCest | null) => void;
}) {
    const [enabled, setEnabled] = useState(false);
    const [search, setSearch] = useState('');
    const [searchDebounce] = useDebouncedValue(search, DEBOUNCE_TIME);

    const params: ApiProdutoCestGetParams = {
        pesquisar: searchDebounce,
    };

    const { data, isFetching } = useQuery({
        enabled,
        queryFn: () => apiProdutoCestGet(params),
        queryKey: ['apiProdutoCestGet', params],
    });

    const options: { value: string; label: string; cest: ProdutoCest | null }[] =
        !Array.isArray(data?.registros) || data.registros.length === 0 || !enabled
            ? [
                  {
                      cest: null,
                      label: `${value ?? ''} - ${description ?? ''}`,
                      value: value ?? '',
                  },
              ]
            : data.registros.map((n) => ({
                  cest: n,
                  label: n.descricao,
                  value: n.prc_codigo,
              }));

    const handleChange = (
        cest: string | undefined,
        option: BaseOptionType | BaseOptionType[] | undefined,
    ): void => {
        if (!onChange) {
            return;
        }

        if (!(cest && option) || Array.isArray(option)) {
            onChange(null);
            return;
        }

        onChange(option.cest);
    };

    const handleSearch = (value: string): void => {
        setEnabled(true);
        setSearch(value);
    };

    return (
        <Select
            allowClear={true}
            className='w-full'
            disabled={disabled}
            notFoundContent={isFetching ? 'Pesquisando...' : undefined}
            onChange={handleChange}
            options={options}
            showSearch={{
                filterOption,
                onSearch: handleSearch,
                searchValue: search,
            }}
            value={value}
        />
    );
}
